import { UnfurlItem } from '@lib/external/unfurl';
import { isMediaDuplicate } from '@lib/utils/chooseMediaUtils';

export type MediaStatusType = 'error' | 'warning' | 'in-library';

/** Duration (in seconds) above which a media item is flagged as an error. 10 hours. */
export const ERROR_DURATION_THRESHOLD = 36_000;

/**
 * Prefilled Intercom message for the "Contact us" link shown when media exceeds
 * the 10-hour limit. Trailing space is intentional — the user continues typing
 * their details after it.
 */
export const OVER_DURATION_LIMIT_CONTACT_MESSAGE =
  'I would like to upload a video that is longer than the 10 hour limit. Here are some extra details, if needed: ';

/** Percentile of long-form durations used as the baseline for warning detection. */
export const WARNING_PERCENTILE = 0.95;

/** A media item is flagged as a warning if its duration exceeds the percentile × this multiplier. */
export const WARNING_PERCENTILE_MULTIPLIER = 5;

/** Minimum duration (in seconds) for a media item to be considered a warning. 1 hour. */
export const WARNING_DURATION_FLOOR = 3_600;

/** Maximum duration (in seconds) for a video to be considered a "short". 3 minutes. */
export const SHORT_VIDEO_THRESHOLD = 180;

/**
 * Compute the warning percentile duration of long-form items (shorts filtered out).
 * Returns 0 if no long-form items have a duration.
 */
export function computeWarningPercentileDuration(items: UnfurlItem[]): number {
  const durations = items
    .map((item) => item.duration ?? 0)
    .filter((d) => d > SHORT_VIDEO_THRESHOLD);
  if (durations.length === 0) return 0;

  durations.sort((a, b) => a - b);
  const index = Math.ceil(durations.length * WARNING_PERCENTILE) - 1;
  return durations[Math.min(index, durations.length - 1)];
}

/**
 * Compute the status for a single media item.
 *
 * Priority (highest to lowest):
 * 1. In Library — item already exists in library
 * 2. Error — duration > 10 hours
 * 3. Warning — duration outlier (> percentile of long-form × multiplier AND > floor)
 *
 * Returns null if no status applies.
 */
export function computeMediaStatus(
  item: UnfurlItem,
  percentileDuration: number,
  existingUniqueIds: Record<string, string>,
): MediaStatusType | null {
  // 1. In Library (highest priority)
  if (isMediaDuplicate(item, existingUniqueIds)) {
    return 'in-library';
  }

  const duration = item.duration ?? 0;

  // 2. Error — duration > 10 hours
  if (duration > ERROR_DURATION_THRESHOLD) {
    return 'error';
  }

  // 3. Warning — duration outlier (skip when no long-form baseline exists)
  const warningThreshold = percentileDuration * WARNING_PERCENTILE_MULTIPLIER;
  if (percentileDuration > 0 && duration > warningThreshold && duration > WARNING_DURATION_FLOOR) {
    return 'warning';
  }

  return null;
}
